/**
 * Manages server configuration properties.

    ServerConfigManager tracks loadConfigData/loadUserData properties. The latter can change on each
    call to /db/postlogin; the former should not change.

    FeatureFlagManager tracks loadFeatureFlag properties. These should not change, and thus no
    `:register` event is available.
 */

import Backbone from 'backbone';
import Radio from 'backbone.radio';


const configChannel = Radio.channel('config');

/**
 * This came about as part of #2136, where we needed to ensure that specific strings were formatted
    in a specific way, depending on language. Right now, the only property used in this config object
    is `withClass`; if this is set to true and the third param to `configChannel.request('property')`
    is set to true, an HTML element will wrap around the value to ensure correct display.
 */
const configConfig = Object.freeze({
    fullTitle: {
        withClass: true
    },
    site: {
        withClass: true
    },
    siteTitle: {
        withClass: true
    }
});

export class ServerConfigManager extends Backbone.Model
{
    constructor(data, options)
    {
        super(data, options);

        // Proxy to `set`
        configChannel.on('property:register', this.registerProperty, this);

        // Proxy to `get`
        configChannel.reply('property', this.getProperty, this);

        // Track initial load time; used to pass data back and forth between the server
        this.set('initLoad', new Date().getTime().toString());

        const ogDesc = document.querySelector('meta[property="og:description"]');
        if (ogDesc) this.set('ogDescription', ogDesc.getAttribute('content'));
    }

    registerProperty(name, value)
    {
        return this.set(name, value);
    }

    getProperty(name, asHTML)
    {
        let value = this.get(name);
        if (asHTML !== true) return value;

        // if there's no config object to return, there's no formatting to add
        const configObject = configConfig[name];
        if (!configObject) return value;

        // Get all the classes we should tack on to the wrapping element
        let parentElClasses = [];
        if (configObject.withClass)
            parentElClasses.push('config-' + name);

        // Always tack on a `notranslate`, because we want the literal value here, no matter what
        return `<span class="notranslate ${parentElClasses.join(' ')}">${value}</span>`;
    }
}

export class FeatureFlagManager extends Backbone.Model
{
    constructor(data, options)
    {
        super(data, options);

        // Proxy to `get`
        configChannel.reply('flag', this.get, this);
    }
}
